import type { StreamEvent, SSEEvent } from './types';

export class SSEFetcher {
  private controller: AbortController | null = null;
  private reader: ReadableStreamDefaultReader<Uint8Array> | null = null;

  /**
   * Connect to SSE endpoint and start streaming events
   * @param url The URL to connect to
   * @param options Fetch options (headers, method, body, etc.)
   * @param onEvent Callback for each parsed event
   * @param onError Callback for parsing errors
   */
  async connect(
    url: string,
    options: RequestInit,
    onEvent: (event: StreamEvent) => void,
    onError: (error: Error) => void
  ): Promise<void> {
    this.controller = new AbortController();

    const response = await fetch(url, {
      ...options,
      signal: this.controller.signal,
      headers: {
        ...options.headers,
        'Accept': 'text/event-stream',
      },
    });

    if (!response.ok) {
      throw new Error(`SSE connection failed: ${response.status}`);
    }

    if (!response.body) {
      throw new Error('Response body is null');
    }

    this.reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    try {
      while (true) {
        const { done, value } = await this.reader.read();

        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const result = this.parseSSEBuffer(buffer);

        for (const sseEvent of result.parsed) {
          if (sseEvent.data) {
            try {
              const data = JSON.parse(sseEvent.data);
              onEvent(data);
            } catch (err) {
              onError(new Error(`Failed to parse event: ${err}`));
            }
          }
        }

        buffer = result.remaining;
      }
    } catch (error) {
      // If it's an abort error, rethrow it
      if ((error as Error).name === 'AbortError') {
        throw error;
      }
      // Otherwise, it's a read error
      throw error;
    }
  }

  /**
   * Parse SSE buffer into events
   * Returns parsed events and remaining buffer
   */
  private parseSSEBuffer(buffer: string): { parsed: SSEEvent[]; remaining: string } {
    const lines = buffer.split('\n');
    const remaining = lines.pop() || ''; // Keep incomplete line in buffer
    const events: SSEEvent[] = [];

    let currentEvent: Partial<SSEEvent> = {};

    for (const line of lines) {
      // Comment lines (ignored)
      if (line.startsWith(':')) {
        continue;
      }

      // Empty line = event boundary
      if (line === '') {
        if (currentEvent.data) {
          events.push(currentEvent as SSEEvent);
        }
        currentEvent = {};
        continue;
      }

      // Parse field
      const colonIndex = line.indexOf(':');
      if (colonIndex === -1) {
        // Line without colon - ignore
        continue;
      }

      const field = line.substring(0, colonIndex);
      let value = line.substring(colonIndex + 1);

      // Remove leading space after colon (SSE spec)
      if (value.startsWith(' ')) {
        value = value.substring(1);
      }

      if (field === 'data') {
        currentEvent.data = value;
      } else if (field === 'id') {
        currentEvent.id = value;
      } else if (field === 'event') {
        currentEvent.event = value;
      }
      // Other fields like 'retry' are ignored
    }

    return { parsed: events, remaining };
  }

  /**
   * Disconnect and clean up resources
   */
  disconnect(): void {
    if (this.reader) {
      this.reader.cancel().catch(() => {
        // Ignore cancellation errors
      });
      this.reader = null;
    }

    if (this.controller) {
      this.controller.abort();
      this.controller = null;
    }
  }
}
